// GIF Creation Feature Types

/**
 * Playback mode for the generated GIF
 * - one-shot: Play once and stop on last frame
 * - loop: Continuously loop from start to end
 * - bounce: Play forward then backward (ping-pong)
 */
export type GifPlaybackMode = 'one-shot' | 'loop' | 'bounce';

/**
 * Quality preset for GIF output
 */
export type GifQualityPreset = 'low' | 'medium' | 'high' | 'custom';

/**
 * A frame selected for GIF creation with ordering
 */
export interface GifFrame {
  id: string;
  sourceFrameId: string;  // Reference to CapturedFrame.id
  imageData: string;      // Base64 image data
  order: number;          // Position in sequence
  duration?: number;      // Optional per-frame duration override (ms)
  width: number;
  height: number;
}

/**
 * Position preset for text overlays (corners + center positions)
 */
export type TextPositionPreset =
  | 'top-center'
  | 'bottom-center'
  | 'center'
  | 'top-left'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-right';

/**
 * Position for a text overlay - either a preset or custom percentage coords
 */
export type TextPosition =
  | { kind: 'preset'; preset: TextPositionPreset }
  | { kind: 'custom'; x: number; y: number }; // 0-100 percentages

/**
 * Available font families for text overlays (system fonts only in v1)
 */
export type TextFontFamily = 'sans-serif' | 'serif' | 'monospace';

/**
 * A text/caption overlay applied to GIF frames
 */
export interface TextOverlay {
  id: string;
  content: string;
  position: TextPosition;
  fontSize: number;       // 12-72px
  fontFamily: TextFontFamily;
  color: string;          // hex
  strokeColor: string;    // hex
  strokeWidth: number;    // 0-4px
  // Frame range to apply text to: 'all' or [startInclusive, endInclusive] indices
  frameRange: 'all' | [number, number];
}

/**
 * Aspect ratio presets for cropping
 */
export type AspectRatioPreset = 'free' | '1:1' | '16:9' | '4:3' | '9:16';

/**
 * Crop region in normalized 0-1 coordinates relative to original frame
 */
export interface CropRegion {
  x: number;       // 0-1
  y: number;       // 0-1
  width: number;   // 0-1
  height: number;  // 0-1
  aspectRatio: AspectRatioPreset;
}

/**
 * Settings for GIF generation
 */
export interface GifSettings {
  playbackMode: GifPlaybackMode;
  frameRate: number;           // FPS (1-30)
  quality: GifQualityPreset;
  maxWidth: number;            // Max output width (height scales proportionally)
  loop: number;                // 0 = infinite, 1+ = loop count
  defaultFrameDuration: number; // Default ms per frame (derived from frameRate)
  // F8: Speed multiplier - applied to frame delays during encoding (0.25 = quarter speed)
  speedMultiplier: number;     // 0.25 - 4
  // F2: Watermark for guest/anonymous output
  watermark?: WatermarkSettings;
  // F4: Text overlays burned into output
  textOverlays?: TextOverlay[];
  // F5: Crop region applied during encoding
  crop?: CropRegion;
}

/**
 * Watermark settings burned into GIF for guest users
 */
export interface WatermarkSettings {
  enabled: boolean;
  text: string;            // e.g. "FrameX"
  opacity: number;         // 0-1
}

/**
 * Default GIF settings
 */
export const DEFAULT_GIF_SETTINGS: GifSettings = {
  playbackMode: 'loop',
  frameRate: 10,
  quality: 'medium',
  maxWidth: 480,
  loop: 0,
  defaultFrameDuration: 100,
  speedMultiplier: 1,
  watermark: { enabled: false, text: 'FrameX', opacity: 0.5 },
  textOverlays: [],
  crop: undefined,
};

/**
 * Quality preset configurations
 */
export const QUALITY_PRESETS: Record<GifQualityPreset, { maxWidth: number; colors: number }> = {
  low: { maxWidth: 320, colors: 64 },
  medium: { maxWidth: 480, colors: 128 },
  high: { maxWidth: 640, colors: 256 },
  custom: { maxWidth: 480, colors: 256 },
};

/**
 * Message types for Web Worker communication
 */
export interface GifEncoderRequest {
  type: 'encode';
  frames: GifFrame[];
  settings: GifSettings;
}

export interface GifEncoderProgress {
  type: 'progress';
  percent: number;
  currentFrame: number;
  totalFrames: number;
}

export interface GifEncoderComplete {
  type: 'complete';
  gifBlob: Blob;
  gifDataUrl: string;
  fileSize: number;
  frameCount: number;
  duration: number; // Total duration in ms
}

export interface GifEncoderError {
  type: 'error';
  message: string;
}

export type GifEncoderResponse = GifEncoderProgress | GifEncoderComplete | GifEncoderError;

/**
 * State for the GIF creator
 */
export interface GifCreatorState {
  frames: GifFrame[];
  settings: GifSettings;
  isEncoding: boolean;
  encodingProgress: number;
  previewUrl: string | null;
  outputBlob: Blob | null;
  error: string | null;
  isSaving: boolean;
  savedGifId: string | null;
}

/**
 * Frame limits and warnings
 */
export const GIF_LIMITS = {
  WARNING_FRAME_COUNT: 50,
  MAX_FRAME_COUNT: 200,
  MIN_FPS: 1,
  MAX_FPS: 30,
  MIN_DURATION_MS: 20,
  MAX_DURATION_MS: 5000,
};

/**
 * Convert FPS to frame duration in milliseconds
 */
export function fpsToMs(fps: number): number {
  return Math.round(1000 / fps);
}

/**
 * Convert frame duration to FPS
 */
export function msToFps(ms: number): number {
  return Math.round(1000 / ms);
}

/**
 * Calculate estimated GIF duration
 */
export function calculateGifDuration(frames: GifFrame[], settings: GifSettings): number {
  const baseDuration = frames.reduce((total, frame) => {
    return total + (frame.duration || settings.defaultFrameDuration);
  }, 0);
  
  // For bounce mode, the animation plays forward then backward (minus one duplicate)
  if (settings.playbackMode === 'bounce') {
    return baseDuration + (frames.length > 1 ? baseDuration - settings.defaultFrameDuration * 2 : 0);
  }
  
  return baseDuration;
}

/**
 * Prepare frames for encoding based on playback mode
 */
export function prepareFramesForEncoding(frames: GifFrame[], settings: GifSettings): GifFrame[] {
  const sortedFrames = [...frames].sort((a, b) => a.order - b.order);
  
  if (settings.playbackMode === 'bounce' && sortedFrames.length > 1) {
    // Add reversed frames (excluding first and last to avoid duplicates)
    const reversedFrames = sortedFrames
      .slice(1, -1)
      .reverse()
      .map((frame, index) => ({
        ...frame,
        id: `${frame.id}-bounce-${index}`,
        order: sortedFrames.length + index,
      }));
    
    return [...sortedFrames, ...reversedFrames];
  }
  
  return sortedFrames;
}

// ============================================================================
// F10: GIF Templates - One-click presets for common use cases
// ============================================================================

export type GifTemplateId =
  | 'custom'
  | 'reaction'
  | 'tutorial'
  | 'meme'
  | 'cinemagraph'
  | 'social-square'
  | 'story-vertical'
  | 'chat'
  | 'email';

export interface GifTemplate {
  id: GifTemplateId;
  name: string;
  description: string;
  icon: string;        // emoji or lucide icon name
  settings: Partial<GifSettings>;
}

export const GIF_TEMPLATES: GifTemplate[] = [
  {
    id: 'custom',
    name: 'Custom',
    description: 'Configure all settings manually',
    icon: '⚙️',
    settings: {},
  },
  {
    id: 'reaction',
    name: 'Reaction GIF',
    description: 'Quick & punchy for social responses',
    icon: '⚡',
    settings: {
      frameRate: 15,
      quality: 'medium',
      maxWidth: 320,
      playbackMode: 'loop',
      loop: 0,
    },
  },
  {
    id: 'tutorial',
    name: 'Tutorial',
    description: 'Slow & clear for step-by-step demos',
    icon: '📚',
    settings: {
      frameRate: 5,
      quality: 'high',
      maxWidth: 640,
      playbackMode: 'loop',
      loop: 0,
    },
  },
  {
    id: 'meme',
    name: 'Meme',
    description: 'Standard sharing-friendly format',
    icon: '😂',
    settings: {
      frameRate: 10,
      quality: 'medium',
      maxWidth: 480,
      playbackMode: 'loop',
      loop: 0,
    },
  },
  {
    id: 'cinemagraph',
    name: 'Cinemagraph',
    description: 'Smooth motion for artistic loops',
    icon: '🎬',
    settings: {
      frameRate: 24,
      quality: 'high',
      maxWidth: 640,
      playbackMode: 'loop',
      loop: 0,
    },
  },
  {
    id: 'social-square',
    name: 'Social Square',
    description: 'Square format for Instagram/feeds',
    icon: '📱',
    settings: {
      frameRate: 12,
      quality: 'high',
      maxWidth: 480,
      playbackMode: 'loop',
      loop: 0,
    },
  },
  {
    id: 'story-vertical',
    name: 'Story (Vertical)',
    description: '9:16 for stories & reels',
    icon: '📲',
    settings: {
      frameRate: 12,
      quality: 'high',
      maxWidth: 360,
      playbackMode: 'loop',
      loop: 0,
    },
  },
  {
    id: 'chat',
    name: 'Slack/Discord',
    description: 'Tiny size for chat reactions',
    icon: '💬',
    settings: {
      frameRate: 10,
      quality: 'low',
      maxWidth: 256,
      playbackMode: 'loop',
      loop: 0,
    },
  },
  {
    id: 'email',
    name: 'Email Header',
    description: 'Lightweight one-shot for newsletters',
    icon: '✉️',
    settings: {
      frameRate: 8,
      quality: 'low',
      maxWidth: 320,
      playbackMode: 'one-shot',
      loop: 1,
    },
  },
];

/**
 * Resolve aspect ratio numeric value (width/height) for a preset
 */
export function getAspectRatioValue(preset: AspectRatioPreset): number | null {
  switch (preset) {
    case '1:1':  return 1;
    case '16:9': return 16 / 9;
    case '4:3':  return 4 / 3;
    case '9:16': return 9 / 16;
    case 'free':
    default:     return null;
  }
}

// ============================================================================
// F2: Guest mode constants
// ============================================================================

export const GUEST_LIMITS = {
  MAX_FRAMES: 5,
  MAX_VIDEO_RANGE_SECONDS: 10,
  MAX_OUTPUT_WIDTH: 480,
};

/**
 * Build a default watermark for guest users
 */
export function buildGuestWatermark(): WatermarkSettings {
  return { enabled: true, text: 'FrameX', opacity: 0.55 };
}
